import { type AnyAction, combineReducers } from 'redux';
import Immutable from 'immutable';
import type { OpaqueVideoData } from '@lumen5/luminary';
import { Migrator, VideoData } from '@lumen5/luminary';

import { ImmutableFromJS } from '../../libs/consts';

import type {
    DisplayGroup,
    SceneDesign,
    SceneDesignIdsPerTemplate,
    Template,
    TemplateCategory,
} from 'js/src/client';
import StoreORM from 'js/src/data/store-orm';
import loadingInfoSlice from 'js/src/data/templates/slices/loadingInfoSlice';
import {
    _setTemplateData,
    setActiveTemplateCategoryId,
    setDisplayGroupName,
    setNonRecommendableAssetNames,
    setSceneDesignAttributesRecommendation,
    setSceneDesignData,
    setSceneDesignDisplayGroup,
    setSceneDesignDisplayName,
    setSceneDesignOrders,
    setSceneDesignStatus,
    setTemplateAspectRatioMp4Url,
    setTemplateCategoryId,
    setTemplateColorPalettes,
    setTemplateColorShuffleEnabled,
    setTemplateDefaultCaptionSceneDesign,
    setTemplateDefaultCutawaySceneDesign,
    setTemplateDefaultIconSceneDesign,
    setTemplateDefaultOutroSceneDesign,
    setTemplateDefaultSceneDesign,
    setTemplateDefaultStoryboardSceneDesign,
    setTemplateDefaultSwapFancyCaptionAssetStyleSceneDesign,
    setTemplateDefaultSwapTextAssetStyleSceneDesign,
    setTemplateDefaultTalkingHeadSceneDesign,
    setTemplateDefaultTextSizeOptions,
    setTemplateDefaultVoiceoverSceneDesign,
    setTemplateDisplayName,
    setTemplateImageUrl,
    setTemplateMp4Url,
    setTemplateOrders,
    setTemplateSearchQuery,
    setTemplateStatus,
    setTemplateTextMaxSize,
    setTemplateTextMinSize,
    storeAllSceneDesignIdsPerTemplate,
    storeDisplayGroups,
    storeRecentSelectedSceneDesign,
    storeSceneDesign,
    storeSceneDesigns,
    storeTemplate,
    storeTemplateCategories,
    storeTemplates,
} from 'js/src/data/templates/templates-actions';
import type { ImmutableMap, TemplateCategoryId } from 'js/src/libs/consts';

export function templateCategories(
    state = StoreORM.newFromIdPrefix<TemplateCategory>('templateCategory').data,
    action: AnyAction,
) {
    if (storeTemplateCategories.match(action)) {
        const orm = new StoreORM<TemplateCategory>(state);
        action.payload.categories.forEach((category) => {
            orm.pushJSItem(category, 'id');
        });
        return orm.data;
    }
    return state;
}

export function templatesList(
    state = StoreORM.newFromIdPrefix<Template, string>('templatesList').data,
    action: AnyAction,
) {
    if (storeTemplates.match(action)) {
        const orm = new StoreORM<Template, string>(state);
        const templates = action.payload.templates.sort((sdl, sdr) => sdl.order - sdr.order);
        templates.forEach((template) => {
            orm.pushJSItem(
                {
                    ...template,
                    template_data: Migrator.updateTemplateData(
                        template.template_data as OpaqueVideoData,
                    ),
                },
                'id',
            );
        });
        return orm.data;
    }
    if (storeTemplate.match(action)) {
        const orm = new StoreORM<Template, string>(state);
        const template = {
            ...action.payload.template,
            template_data: Migrator.updateTemplateData(
                action.payload.template.template_data as OpaqueVideoData,
            ),
        };
        orm.pushJSItem(template, 'id');
        return orm.data;
    }
    if (_setTemplateData.match(action)) {
        const orm = new StoreORM<Template, string>(state);
        orm.updateById(action.payload.templateId, {
            key: ['template_data'],
            value: Immutable.fromJS(action.payload.data),
        });
        return orm.data;
    }
    if (setTemplateStatus.match(action)) {
        const orm = new StoreORM<Template, string>(state);
        orm.updateById(action.payload.templateId, {
            key: ['status'],
            value: action.payload.status,
        });
        return orm.data;
    }
    if (setTemplateColorPalettes.match(action)) {
        const orm = new StoreORM<Template, string>(state);
        orm.updateById(action.payload.templateId, {
            key: ['palette_data'],
            value: action.payload.palettes,
        });
        return orm.data;
    }
    if (setTemplateDisplayName.match(action)) {
        const orm = new StoreORM<Template, string>(state);
        orm.updateById(action.payload.templateId, {
            key: ['display_name'],
            value: action.payload.displayName,
        });
        return orm.data;
    }
    if (setTemplateDefaultStoryboardSceneDesign.match(action)) {
        const orm = new StoreORM<Template, string>(state);
        orm.updateById(action.payload.templateId, {
            key: ['default_storyboard_scene_design'],
            value: action.payload.sceneDesignId,
        });
        return orm.data;
    }
    if (setTemplateDefaultOutroSceneDesign.match(action)) {
        const orm = new StoreORM<Template, string>(state);
        orm.updateById(action.payload.templateId, {
            key: ['default_outro_scene_design'],
            value: action.payload.sceneDesignId,
        });
        return orm.data;
    }
    if (setTemplateDefaultIconSceneDesign.match(action)) {
        const orm = new StoreORM<Template, string>(state);
        orm.updateById(action.payload.templateId, {
            key: ['default_icon_scene_design'],
            value: action.payload.sceneDesignId,
        });
        return orm.data;
    }
    if (setTemplateDefaultSceneDesign.match(action)) {
        const orm = new StoreORM<Template, string>(state);
        orm.updateById(action.payload.templateId, {
            key: ['default_scene_design'],
            value: action.payload.sceneDesignId,
        });
        return orm.data;
    }
    if (setTemplateDefaultTalkingHeadSceneDesign.match(action)) {
        const orm = new StoreORM<Template, string>(state);
        orm.updateById(action.payload.templateId, {
            key: ['default_talking_head_scene_design'],
            value: action.payload.sceneDesignId,
        });
        return orm.data;
    }
    if (setTemplateDefaultCutawaySceneDesign.match(action)) {
        const orm = new StoreORM<Template, string>(state);
        orm.updateById(action.payload.templateId, {
            key: ['default_cutaway_scene_design'],
            value: action.payload.sceneDesignId,
        });
        return orm.data;
    }
    if (setTemplateDefaultCaptionSceneDesign.match(action)) {
        const orm = new StoreORM<Template, string>(state);
        orm.updateById(action.payload.templateId, {
            key: ['default_caption_scene_design'],
            value: action.payload.sceneDesignId,
        });
        return orm.data;
    }
    if (setTemplateDefaultVoiceoverSceneDesign.match(action)) {
        const orm = new StoreORM<Template, string>(state);
        orm.updateById(action.payload.templateId, {
            key: ['default_voiceover_scene_design'],
            value: action.payload.sceneDesignId,
        });
        return orm.data;
    }
    if (setTemplateDefaultSwapTextAssetStyleSceneDesign.match(action)) {
        const orm = new StoreORM<Template, string>(state);
        orm.updateById(action.payload.templateId, {
            key: ['default_swap_text_asset_style_scene_design'],
            value: action.payload.sceneDesignId,
        });
        return orm.data;
    }
    if (setTemplateDefaultSwapFancyCaptionAssetStyleSceneDesign.match(action)) {
        const orm = new StoreORM<Template, string>(state);
        orm.updateById(action.payload.templateId, {
            key: ['default_swap_fancy_caption_asset_style_scene_design'],
            value: action.payload.sceneDesignId,
        });
        return orm.data;
    }
    if (setTemplateImageUrl.match(action)) {
        const orm = new StoreORM<Template, string>(state);
        orm.updateById(action.payload.templateId, {
            key: ['image_url'],
            value: action.payload.imageUrl,
        });
        return orm.data;
    }
    if (setTemplateMp4Url.match(action)) {
        const orm = new StoreORM<Template, string>(state);
        orm.updateById(action.payload.templateId, {
            key: ['mp4_url'],
            value: action.payload.mp4Url,
        });
        return orm.data;
    }
    if (setTemplateAspectRatioMp4Url.match(action)) {
        const orm = new StoreORM<Template, string>(state);
        const key = [`${action.payload.aspectRatio}_mp4_url`];
        orm.updateById(action.payload.templateId, {
            key,
            value: action.payload.mp4Url,
        });
        return orm.data;
    }
    if (setTemplateTextMinSize.match(action)) {
        const orm = new StoreORM<Template, string>(state);
        orm.updateById(action.payload.templateId, {
            key: ['text_min_size'],
            value: action.payload.textMinSize,
        });
        return orm.data;
    }
    if (setTemplateTextMaxSize.match(action)) {
        const orm = new StoreORM<Template, string>(state);
        orm.updateById(action.payload.templateId, {
            key: ['text_max_size'],
            value: action.payload.textMaxSize,
        });
        return orm.data;
    }
    if (setTemplateOrders.match(action)) {
        const orm = new StoreORM<Template, string>(state);
        action.payload.orderedTemplateIds.forEach((templateId, newOrder) => {
            orm.updateById(templateId, {
                key: ['order'],
                value: newOrder,
            });
        });
        const newOrderOrm = StoreORM.newFromIdPrefix<Template, string>('templatesList');
        action.payload.orderedTemplateIds.forEach((templateId) => {
            newOrderOrm.pushItem(orm.getById(templateId), 'id');
        });
        return newOrderOrm.data;
    }
    if (setTemplateCategoryId.match(action)) {
        const orm = new StoreORM<Template, string>(state);
        orm.updateById(action.payload.templateId, {
            key: ['template_category'],
            value: action.payload.categoryId,
        });
        return orm.data;
    }
    if (setTemplateDefaultTextSizeOptions.match(action)) {
        const orm = new StoreORM<Template, string>(state);
        orm.updateById(action.payload.templateId, {
            key: ['default_text_size_options'],
            value: action.payload.options,
        });
        return orm.data;
    }
    if (setTemplateColorShuffleEnabled.match(action)) {
        const orm = new StoreORM<Template, string>(state);
        orm.updateById(action.payload.templateId, {
            key: ['color_shuffle_enabled'],
            value: action.payload.color_shuffle_enabled,
        });
        return orm.data;
    }
    return state;
}

export function sceneDesignIdsByTemplate(
    state = StoreORM.newFromIdPrefix<SceneDesignIdsPerTemplate, string>('sceneDesignIdsByTemplate')
        .data,
    action: AnyAction,
) {
    if (storeTemplates.match(action)) {
        const orm = new StoreORM<SceneDesignIdsPerTemplate, string>(state);
        action.payload.templates.forEach((template) => {
            const templateId = template.id;
            if (!orm.contains(templateId)) {
                orm.pushJSItem({ template_id: template.id, scene_design_ids: [] }, 'template_id');
            }
        });
        return orm.data;
    }
    if (storeTemplate.match(action)) {
        const orm = new StoreORM<SceneDesignIdsPerTemplate, string>(state);
        const template = action.payload.template;
        const templateId = template.id;
        if (!orm.contains(templateId)) {
            orm.pushJSItem({ template_id: template.id, scene_design_ids: [] }, 'template_id');
        }
        return orm.data;
    }
    if (storeSceneDesigns.match(action)) {
        const orm = new StoreORM<SceneDesignIdsPerTemplate, string>(state);
        const sceneDesigns = action.payload.sceneDesigns.sort((sdl, sdr) => sdl.order - sdr.order);
        sceneDesigns.forEach((sceneDesign) => {
            const templateId = sceneDesign.template;
            if (!orm.contains(templateId)) {
                throw new Error(
                    `Template ${templateId} not loaded. Wait for "loadTemplates()" to finish before calling "loadSceneDesignsForTemplate()". Alternatively, just use "loadTemplateAndSceneDesigns()" to prevent this error.`,
                );
            }
            orm.updateById(templateId, {
                value: (scene_design_ids: string[]) => {
                    if (!scene_design_ids.includes(sceneDesign.id)) {
                        return scene_design_ids.push(sceneDesign.id);
                    }
                    return scene_design_ids;
                },
                key: ['scene_design_ids'],
            });
        });
        return orm.data;
    }
    if (storeSceneDesign.match(action)) {
        const orm = new StoreORM<SceneDesignIdsPerTemplate, string>(state);
        const sceneDesign = action.payload.sceneDesign;
        const templateId = sceneDesign.template;
        if (!orm.contains(templateId)) {
            throw new Error(
                `Template ${templateId} not loaded. Wait for "loadTemplates()" to finish before calling "loadSceneDesignsForTemplate()". Alternatively, just use "loadTemplateAndSceneDesigns()" to prevent this error.`,
            );
        }
        orm.updateById(templateId, {
            value: (scene_design_ids: string[]) => {
                if (!scene_design_ids.includes(sceneDesign.id)) {
                    return scene_design_ids.push(sceneDesign.id);
                }
                return scene_design_ids;
            },
            key: ['scene_design_ids'],
        });
        return orm.data;
    }
    if (setSceneDesignOrders.match(action)) {
        const orm = new StoreORM<SceneDesignIdsPerTemplate, string>(state);
        orm.updateById(action.payload.templateId, {
            value: (scene_design_ids: string[]) =>
                Immutable.fromJS(action.payload.orderedSceneDesignIds),
            key: ['scene_design_ids'],
        });
        return orm.data;
    }
    if (storeAllSceneDesignIdsPerTemplate.match(action)) {
        const orm = new StoreORM<SceneDesignIdsPerTemplate, string>(state);
        action.payload.allSceneDesignIdsPerTemplate.forEach((sceneDesignIdsPerTemplate) => {
            orm.updateById(sceneDesignIdsPerTemplate.template_id, {
                value: Immutable.fromJS(sceneDesignIdsPerTemplate.scene_design_ids),
                key: ['scene_design_ids'],
            });
        });
        return orm.data;
    }
    return state;
}

// TODO: LU-3743 -- this should probably be implemented in Migrator instead
function migrateSceneDesign(sceneDesign: SceneDesign): SceneDesign {
    return VideoData.ALL_ASPECT_RATIOS.reduce(
        (acc, aspectRatio) => {
            const dataKey = `${aspectRatio}_data` as keyof SceneDesign;
            const sceneDesignData = sceneDesign[dataKey];

            if (!sceneDesignData) {
                return acc;
            }

            Object.assign(acc, {
                [dataKey]: Migrator.updateSceneDesignData(sceneDesignData as OpaqueVideoData),
            });
            return acc;
        },
        { ...sceneDesign },
    );
}

export function sceneDesigns(
    state = StoreORM.newFromIdPrefix<SceneDesign, string>('sceneDesigns').data,
    action: AnyAction,
) {
    if (storeSceneDesigns.match(action)) {
        const orm = new StoreORM<SceneDesign, string>(state);
        action.payload.sceneDesigns.forEach((sceneDesign) => {
            orm.pushJSItem(migrateSceneDesign(sceneDesign), 'id');
        });
        return orm.data;
    }
    if (storeSceneDesign.match(action)) {
        const orm = new StoreORM<SceneDesign, string>(state);
        orm.pushJSItem(migrateSceneDesign(action.payload.sceneDesign), 'id');
        return orm.data;
    }
    if (setSceneDesignAttributesRecommendation.match(action)) {
        const orm = new StoreORM<SceneDesign, string>(state);
        orm.updateById(action.payload.sceneDesignId, {
            key: ['scene_attributes_recommendation'],
            value: Immutable.fromJS(action.payload.attributesRecommendation),
        });
        return orm.data;
    }
    if (setNonRecommendableAssetNames.match(action)) {
        const orm = new StoreORM<SceneDesign, string>(state);
        orm.updateById(action.payload.sceneDesignId, {
            key: ['non_recommendable_assets'],
            value: Immutable.fromJS(action.payload.nonRecommendableAssetNames),
        });

        return orm.data;
    }
    if (setSceneDesignData.match(action)) {
        const sceneDesignId = action.payload.sceneDesignId;
        const orm = new StoreORM<SceneDesign, string>(state);
        let sceneDesign = orm.getById(sceneDesignId);
        sceneDesign = sceneDesign.merge(
            ImmutableFromJS(action.payload.data),
        ) as unknown as ImmutableMap<SceneDesign>;
        orm.updateById(sceneDesignId, {
            key: [],
            value: sceneDesign,
        });
        return orm.data;
    }
    if (setSceneDesignStatus.match(action)) {
        const orm = new StoreORM<SceneDesign, string>(state);
        orm.updateById(action.payload.sceneDesignId, {
            key: ['status'],
            value: Immutable.fromJS(action.payload.status),
        });
        return orm.data;
    }
    if (setSceneDesignOrders.match(action)) {
        const orm = new StoreORM<SceneDesign, string>(state);
        action.payload.orderedSceneDesignIds.forEach((sceneDesignId, newOrder) => {
            orm.updateById(sceneDesignId, {
                key: ['order'],
                value: newOrder,
            });
        });
        return orm.data;
    }
    if (setSceneDesignDisplayGroup.match(action)) {
        const orm = new StoreORM<SceneDesign, string>(state);
        orm.updateById(action.payload.sceneDesignId, {
            key: ['display_group'],
            value: Immutable.fromJS(action.payload.displayGroupId),
        });
        return orm.data;
    }
    if (setSceneDesignDisplayName.match(action)) {
        const orm = new StoreORM<SceneDesign, string>(state);
        orm.updateById(action.payload.sceneDesignId, {
            key: ['display_name'],
            value: Immutable.fromJS(action.payload.displayName),
        });
        return orm.data;
    }
    return state;
}

export function displayGroups(
    state = StoreORM.newFromIdPrefix<DisplayGroup>('displayGroups').data,
    action: AnyAction,
) {
    if (storeDisplayGroups.match(action)) {
        const orm = new StoreORM<DisplayGroup>(state);
        orm.delete(orm.getAllIds());
        action.payload.displayGroups.forEach((displayGroup) => {
            orm.pushJSItem(displayGroup, 'id');
        });
        return orm.data;
    }
    if (setDisplayGroupName.match(action)) {
        const orm = new StoreORM<DisplayGroup>(state);
        orm.updateById(action.payload.displayGroupId, {
            key: ['display_name'],
            value: Immutable.fromJS(action.payload.displayGroupName),
        });
        return orm.data;
    }
    return state;
}

export function recentSelectedSceneDesigns(state = Immutable.List<string>(), action: AnyAction) {
    if (storeRecentSelectedSceneDesign.match(action)) {
        // remove the entry if it already exists & add it to the top
        if (state.includes(action.payload.sceneDesignId)) {
            state = state.remove(state.indexOf(action.payload.sceneDesignId));
        }
        state = state.unshift(action.payload.sceneDesignId);
        return state;
    }
    return state;
}

export function activeTemplateCategoryId(
    state: TemplateCategoryId | null = null,
    action: AnyAction,
): TemplateCategoryId | null {
    if (setActiveTemplateCategoryId.match(action)) {
        return action.payload.categoryId;
    }
    return state;
}

export function templateSearchQuery(state: string = '', action: AnyAction): string {
    if (setTemplateSearchQuery.match(action)) {
        return action.payload.query;
    }
    return state;
}

export const templates = combineReducers({
    templateCategories,
    templatesList,
    sceneDesignIdsByTemplate,
    sceneDesigns,
    displayGroups,
    recentSelectedSceneDesigns,
    activeTemplateCategoryId,
    templateSearchQuery,
    loadingInfo: loadingInfoSlice.reducer,
});

export type TemplatesState = ReturnType<typeof templates>;
